import { PlusIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';

import Banner, { BannerVariant } from 'components/Banner/Banner';
import Button, { ButtonVariant } from 'components/Button/Button';
import FormControls from 'components/FormControls/FormControls';
import Panel from 'components/Panel/Panel';
import { Rubric } from 'hooks/useGetCompetencyDetails';
import useNotification from 'hooks/useNotification';
import useUpdateCompetencyRubrics from 'hooks/useUpdateCompetencyRubrics';
import { t } from 'locales/i18n';
import RubricDescriptor from 'pages/Competency/RubricDescriptor';

interface CompetencyRubricsProps {
  rubrics: Rubric[];
  interviewUid: string;
  competencyUid: string;
  isLoading?: boolean;
}
interface FormData {
  [FormFields.RUBRICS]: Rubric[];
}

enum FormFields {
  RUBRICS = 'rubrics',
}

const CompetencyRubrics = ({
  rubrics = [],
  interviewUid,
  competencyUid,
  isLoading,
}: CompetencyRubricsProps) => {
  const defaultRubric = { text: '', rating_scale_value: 0, label: '' };
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { updateCompetencyRubrics, isLoading: isUpdateLoading } =
    useUpdateCompetencyRubrics();
  const [isCompetencyRubricsCollapsed, setIsCompetencyRubricsCollapsed] =
    useState(true);
  const queryClient = useQueryClient();
  const defaultFormValues = useMemo(
    () => ({
      [FormFields.RUBRICS]: rubrics.map((rubric) => ({
        rating_scale_value: rubric.rating_scale_value,
        text: rubric.text,
        label: rubric.label,
      })),
    }),
    [rubrics]
  );

  useEffect(() => {
    reset(defaultFormValues);
  }, [rubrics]);

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.RUBRICS]: yup
          .array()
          .of(
            yup.object().shape({
              text: yup.string(),
              rating_scale_value: yup.number().required(),
              label: yup
                .string()
                .required(t('Rubric descriptor title is required'))
                .max(
                  160,
                  t('Rubric descriptor title cannot exceed 160 characters')
                ),
            })
          )
          .test(
            t('More than one'),
            t('Rubrics must have more than one rubric descriptor'),
            (rubrics) =>
              rubrics ? rubrics.length === 0 || rubrics.length > 1 : false
          )
          .test(
            t('Less than than 20'),
            t('Rubrics cannot have more than 20 rubric descriptors'),
            (rubrics) => (rubrics ? rubrics.length <= 20 : false)
          )
          .test(
            t('Consecutive score value'),
            t('Rubric score values must be consecutive.'),
            (rubrics) => {
              if (rubrics) {
                const scoreValues = (
                  rubrics as { rating_scale_value: number }[]
                ).map((rubric) => rubric.rating_scale_value);
                return !scoreValues
                  .sort((a, b) => a - b)
                  .map((value, index, array) =>
                    index !== 0 ? array[index] === array[index - 1] + 1 : true
                  )
                  .includes(false);
              }
              return false;
            }
          ),
      }),
    [FormFields.RUBRICS]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  });

  const { fields, remove, prepend } = useFieldArray({
    control,
    name: FormFields.RUBRICS,
  });

  const onSubmit: SubmitHandler<FormData> = async ({ rubrics }) => {
    await updateCompetencyRubrics(
      {
        rubrics,
        competencyUid,
        interviewUid,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Rubric saved'),
            t('Your rubric was successfully saved')
          );
          queryClient.invalidateQueries({
            queryKey: ['getCompetencyDetails', interviewUid, competencyUid],
          });
        },
        onError: (error) => {
          showErrorNotification(
            t('There was an error updating the rubric.'),
            error.message
          );
        },
      }
    );
  };

  return (
    <Panel
      panelClassName="relative"
      title={t('Rubric')}
      dataTestId="rubricStepCard"
      isLoading={isLoading}
      extra={
        <Button
          variant={ButtonVariant.TRANSPARENT}
          onClick={() => setIsCompetencyRubricsCollapsed((prev) => !prev)}
          buttonText={
            <>
              {isCompetencyRubricsCollapsed
                ? t('Expand All')
                : t('Collapse All')}
              <PlusIcon
                className={`duration-250 mb-1 ml-1 inline-block h-4 w-4 transform justify-center self-center transition ease-in-out ${
                  isCompetencyRubricsCollapsed ? 'rotate-0' : 'rotate-45'
                }`}
              />
            </>
          }
          dataTestId={
            isCompetencyRubricsCollapsed ? t('expandAll') : t('collapseAll')
          }
        />
      }
    >
      <form data-testid="edit-rubric-form" onSubmit={handleSubmit(onSubmit)}>
        {errors[FormFields.RUBRICS]?.message && (
          <Banner
            message={errors[FormFields.RUBRICS]?.message}
            variant={BannerVariant.ERROR}
            bannerDataTestId="rubricError"
          />
        )}
        {fields && (
          <div className="space-y-2">
            {fields
              .map((field, index) => (
                <RubricDescriptor
                  key={index.toString()}
                  fieldName={FormFields.RUBRICS}
                  isOpen={
                    !isCompetencyRubricsCollapsed ||
                    (!field.text && !field.label)
                  }
                  {...{ control, index, field, remove, prepend }}
                />
              ))
              .reverse()}
          </div>
        )}
        <div className="flex justify-between">
          <div className="mt-4 self-end">
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={() => prepend(defaultRubric)}
              buttonText={t('Add Rubric Descriptor')}
              dataTestId="addRubricButton"
            />
          </div>
          {isDirty && (
            <FormControls
              isLoading={isUpdateLoading}
              onCancel={() => reset(defaultFormValues)}
            />
          )}
        </div>
      </form>
    </Panel>
  );
};

export default CompetencyRubrics;
